/**
 * EquipmentPage - Safety Equipment & Asset Register
 * 
 * Features:
 * - List all equipment with filtering by site, category
 * - Stock level tracking with low stock alerts
 * - Asset management with service schedules
 * - Add/edit equipment items
 */
import React, { useState, useEffect } from 'react';
import api, { formatDate } from '../lib/api';
import { EQUIPMENT_CATEGORIES } from '../lib/constants';
import { Card, Spinner, EmptyState, Select } from '../components/shared';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Wrench, Plus, MapPin, Package, AlertCircle, X, Edit2, Archive, TrendingDown } from 'lucide-react';

// Stock status badge
const StockBadge = ({ item }) => {
  if (item.is_asset) {
    return <span className="stock-badge stock-asset">Asset</span>;
  }
  
  if (item.current_qty === 0) {
    return <span className="stock-badge stock-empty" data-testid="stock-empty">Out of Stock</span>;
  }
  if (item.min_qty && item.current_qty < item.min_qty) {
    return <span className="stock-badge stock-low" data-testid="stock-low">Low Stock</span>;
  }
  return <span className="stock-badge stock-ok" data-testid="stock-ok">In Stock</span>;
};

// Category icon
const CategoryIcon = ({ category }) => {
  const config = EQUIPMENT_CATEGORIES[category];
  if (config?.icon) {
    return <img src={config.icon} alt={category} className="category-icon" />;
  }
  if (config?.emoji) {
    return <span className="category-emoji">{config.emoji}</span>;
  }
  return <Wrench size={18} className="category-icon-svg" />;
};

// Add/Edit Modal
const EquipmentFormModal = ({ isOpen, onClose, onSave, item, sites }) => {
  const [form, setForm] = useState({
    site_id: '',
    category: 'PPE',
    name: '',
    description: '',
    location: '',
    current_qty: 0,
    min_qty: 0,
    is_asset: false,
    service_frequency_days: null,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (item) {
      setForm({
        site_id: item.site_id || '',
        category: item.category || 'PPE',
        name: item.name || '',
        description: item.description || '',
        location: item.location || '',
        current_qty: item.current_qty || 0,
        min_qty: item.min_qty || 0,
        is_asset: item.is_asset || false,
        service_frequency_days: item.service_frequency_days || null,
      });
    } else {
      setForm({
        site_id: sites[0]?.id || '',
        category: 'PPE',
        name: '',
        description: '',
        location: '',
        current_qty: 0,
        min_qty: 0,
        is_asset: false,
        service_frequency_days: null,
      });
    }
  }, [item, sites]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.site_id) {
      toast.error('Please fill in required fields');
      return;
    }
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      toast.error(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" data-testid="equipment-form-modal">
      <div className="modal-content modal-md">
        <div className="modal-header">
          <h2>{item ? 'Edit Equipment' : 'Add Equipment'}</h2>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label required">Site</label>
                <select 
                  className="form-select" 
                  value={form.site_id} 
                  onChange={(e) => setForm({...form, site_id: e.target.value})}
                  data-testid="equipment-site-select"
                >
                  {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label required">Category</label>
                <select 
                  className="form-select" 
                  value={form.category}
                  onChange={(e) => setForm({...form, category: e.target.value})}
                  data-testid="equipment-category-select"
                >
                  {Object.entries(EQUIPMENT_CATEGORIES).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group form-group-full">
                <label className="form-label required">Name</label>
                <input 
                  type="text" 
                  className="form-input" 
                  placeholder="e.g. Safety Glasses"
                  value={form.name}
                  onChange={(e) => setForm({...form, name: e.target.value})}
                  data-testid="equipment-name"
                />
              </div>
              <div className="form-group form-group-full">
                <label className="form-label">Description</label>
                <textarea 
                  className="form-textarea" 
                  rows={2}
                  placeholder="Additional details about this equipment"
                  value={form.description}
                  onChange={(e) => setForm({...form, description: e.target.value})}
                />
              </div>
              <div className="form-group form-group-full">
                <label className="form-label">Location</label>
                <input 
                  type="text" 
                  className="form-input" 
                  placeholder="e.g. PPE Cabinet - Workshop"
                  value={form.location}
                  onChange={(e) => setForm({...form, location: e.target.value})}
                  data-testid="equipment-location"
                />
              </div>
              
              <div className="form-group">
                <label className="form-checkbox-label">
                  <input 
                    type="checkbox" 
                    checked={form.is_asset}
                    onChange={(e) => setForm({...form, is_asset: e.target.checked})}
                    data-testid="equipment-is-asset"
                  />
                  <span>Track as Asset (not consumable)</span>
                </label>
              </div>

              {!form.is_asset && (
                <>
                  <div className="form-group">
                    <label className="form-label">Current Quantity</label>
                    <input 
                      type="number" 
                      className="form-input" 
                      min="0"
                      value={form.current_qty}
                      onChange={(e) => setForm({...form, current_qty: parseInt(e.target.value) || 0})}
                      data-testid="equipment-current-qty"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Minimum Stock Level</label>
                    <input 
                      type="number" 
                      className="form-input" 
                      min="0"
                      value={form.min_qty}
                      onChange={(e) => setForm({...form, min_qty: parseInt(e.target.value) || 0})}
                      data-testid="equipment-min-qty"
                    />
                  </div>
                </>
              )}

              {form.is_asset && (
                <div className="form-group">
                  <label className="form-label">Service Frequency (days)</label>
                  <input 
                    type="number" 
                    className="form-input" 
                    min="0"
                    placeholder="e.g. 365"
                    value={form.service_frequency_days || ''}
                    onChange={(e) => setForm({...form, service_frequency_days: parseInt(e.target.value) || null})}
                  />
                </div>
              )}
            </div>
          </div>
          <div className="modal-footer">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={saving} data-testid="equipment-save-btn">
              {saving ? 'Saving...' : (item ? 'Update' : 'Add Item')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Detail Modal
const EquipmentDetailModal = ({ isOpen, onClose, item, onEdit, onAdjustStock }) => {
  const [adjustment, setAdjustment] = useState(0);
  const [adjusting, setAdjusting] = useState(false);

  if (!isOpen || !item) return null;

  const handleAdjust = async () => {
    if (adjustment === 0) return;
    setAdjusting(true);
    try {
      await onAdjustStock(item, adjustment);
      setAdjustment(0);
    } finally {
      setAdjusting(false);
    }
  };

  const categoryConfig = EQUIPMENT_CATEGORIES[item.category] || { label: item.category };

  return (
    <div className="modal-overlay" data-testid="equipment-detail-modal">
      <div className="modal-content modal-md">
        <div className="modal-header">
          <div className="modal-header-content">
            <CategoryIcon category={item.category} />
            <h2>{item.name}</h2>
          </div>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="modal-body">
          <div className="detail-grid">
            <div className="detail-section">
              <h3>Details</h3>
              <div className="detail-row">
                <span className="detail-label">Category</span>
                <span className="detail-value">{categoryConfig.label}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Site</span>
                <span className="detail-value">{item.site_name}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Location</span>
                <span className="detail-value">{item.location || 'Not specified'}</span>
              </div>
              {item.description && (
                <div className="detail-row">
                  <span className="detail-label">Description</span>
                  <span className="detail-value">{item.description}</span>
                </div>
              )}
            </div>
            
            <div className="detail-section">
              <h3>Stock Status</h3>
              <div className="detail-row">
                <span className="detail-label">Type</span>
                <span className="detail-value">{item.is_asset ? 'Asset (tracked)' : 'Consumable'}</span>
              </div>
              {!item.is_asset && (
                <>
                  <div className="detail-row">
                    <span className="detail-label">Current Quantity</span>
                    <span className="detail-value detail-value-large">{item.current_qty}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Minimum Level</span>
                    <span className="detail-value">{item.min_qty || 0}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Status</span>
                    <StockBadge item={item} />
                  </div>
                </>
              )}
              {item.is_asset && item.service_frequency_days && (
                <div className="detail-row">
                  <span className="detail-label">Service Frequency</span>
                  <span className="detail-value">Every {item.service_frequency_days} days</span>
                </div>
              )}
            </div>

            {!item.is_asset && (
              <div className="detail-section detail-section-full">
                <h3>Stock Adjustment</h3>
                <div className="stock-adjustment-row">
                  <div className="stock-adjustment-controls">
                    <button 
                      className="stock-adj-btn" 
                      onClick={() => setAdjustment(Math.max(-item.current_qty, adjustment - 1))}
                      disabled={adjustment <= -item.current_qty}
                    >-</button>
                    <span className={`stock-adj-value ${adjustment > 0 ? 'positive' : adjustment < 0 ? 'negative' : ''}`}>
                      {adjustment > 0 ? '+' : ''}{adjustment}
                    </span>
                    <button 
                      className="stock-adj-btn" 
                      onClick={() => setAdjustment(adjustment + 1)}
                    >+</button>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleAdjust}
                    disabled={adjustment === 0 || adjusting}
                    data-testid="equipment-adjust-stock"
                  >
                    {adjusting ? 'Updating...' : 'Update Stock'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <Button variant="ghost" onClick={onClose}>Close</Button>
          <Button variant="primary" onClick={() => onEdit(item)} data-testid="equipment-edit-btn">
            <Edit2 size={16} /> Edit
          </Button>
        </div>
      </div>
    </div>
  );
};

// Main Page Component
const EquipmentPage = () => {
  const [items, setItems] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ site_id: '', category: '' });
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    loadData();
  }, [filters]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load sites
      const sitesRes = await api.get('/sites');
      setSites(sitesRes.items || []);

      // Load equipment items
      let url = '/equipment';
      const params = new URLSearchParams();
      if (filters.site_id) params.append('site_id', filters.site_id);
      if (filters.category) params.append('category', filters.category);
      if (params.toString()) url += `?${params.toString()}`;
      
      const res = await api.get(url);
      setItems(res.items || []);
    } catch (err) {
      console.error('Failed to load equipment:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (formData) => {
    if (selectedItem) {
      await api.patch(`/equipment/${selectedItem.id}`, formData);
      toast.success('Equipment updated');
    } else {
      await api.post('/equipment', formData);
      toast.success('Equipment added');
    }
    loadData();
    setSelectedItem(null);
  };

  const handleAdjustStock = async (item, adjustment) => {
    try {
      const newQty = item.current_qty + adjustment;
      await api.patch(`/equipment/${item.id}`, { current_qty: newQty });
      toast.success(`Stock updated: ${item.current_qty} → ${newQty}`);
      loadData();
      // Update selected item for detail modal
      setSelectedItem(prev => prev ? { ...prev, current_qty: newQty } : null);
    } catch (err) {
      toast.error('Failed to adjust stock');
    }
  };

  const handleAdd = () => {
    setSelectedItem(null);
    setShowFormModal(true);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setShowDetailModal(false);
    setShowFormModal(true);
  };

  const handleView = (item) => {
    setSelectedItem(item);
    setShowDetailModal(true);
  };

  // Calculate summary stats
  const stats = {
    total: items.length,
    assets: items.filter(item => item.is_asset).length,
    lowStock: items.filter(item => !item.is_asset && item.min_qty && item.current_qty < item.min_qty).length,
    outOfStock: items.filter(item => !item.is_asset && item.current_qty === 0).length,
  };

  return (
    <div className="page equipment-page" data-testid="equipment-page">
      <div className="page-header">
        <div>
          <h1>Equipment & Asset Register</h1>
          <p>Track safety equipment, stock levels, and assets</p>
        </div>
        <Button variant="primary" onClick={handleAdd} data-testid="add-equipment-btn">
          <Plus size={16} /> Add Equipment
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="page-stats" data-testid="equipment-stats">
        <div className="stat-card">
          <Package size={20} className="stat-icon" />
          <div className="stat-content">
            <span className="stat-value">{stats.total}</span>
            <span className="stat-label">Total Items</span>
          </div>
        </div>
        <div className="stat-card">
          <Archive size={20} className="stat-icon" />
          <div className="stat-content">
            <span className="stat-value">{stats.assets}</span>
            <span className="stat-label">Assets</span>
          </div>
        </div>
        <div className={`stat-card ${stats.lowStock > 0 ? 'stat-warning' : ''}`}>
          <TrendingDown size={20} className="stat-icon" />
          <div className="stat-content">
            <span className="stat-value">{stats.lowStock}</span>
            <span className="stat-label">Low Stock</span>
          </div>
        </div>
        <div className={`stat-card ${stats.outOfStock > 0 ? 'stat-danger' : ''}`}>
          <AlertCircle size={20} className="stat-icon" />
          <div className="stat-content">
            <span className="stat-value">{stats.outOfStock}</span>
            <span className="stat-label">Out of Stock</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="filters-card">
        <div className="filters-row">
          <Select
            label="Site"
            value={filters.site_id}
            onChange={(e) => setFilters({ ...filters, site_id: e.target.value })}
            options={[
              { value: '', label: 'All Sites' },
              ...sites.map(s => ({ value: s.id, label: s.name }))
            ]}
          />
          <Select
            label="Category"
            value={filters.category}
            onChange={(e) => setFilters({ ...filters, category: e.target.value })}
            options={[
              { value: '', label: 'All Categories' },
              ...Object.entries(EQUIPMENT_CATEGORIES).map(([k, v]) => ({ value: k, label: v.label }))
            ]}
          />
          {(filters.site_id || filters.category) && (
            <button className="filter-clear-btn" onClick={() => setFilters({ site_id: '', category: '' })}>
              Clear filters
            </button>
          )}
        </div>
      </Card>

      {/* Items List */}
      {loading ? (
        <div className="page-loading"><Spinner /></div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<Wrench size={48} />}
          title="No equipment found"
          description={filters.site_id || filters.category ? "No items match the current filters" : "Add your first equipment item to get started"}
        />
      ) : (
        <Card className="table-card">
          <table className="data-table" data-testid="equipment-table">
            <thead>
              <tr>
                <th>Category</th>
                <th>Name</th>
                <th>Site</th>
                <th>Location</th>
                <th>Quantity</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr 
                  key={item.id} 
                  onClick={() => handleView(item)}
                  className="table-row clickable"
                  data-testid={`equipment-row-${item.id}`}
                >
                  <td>
                    <div className="category-cell">
                      <CategoryIcon category={item.category} />
                      <span>{EQUIPMENT_CATEGORIES[item.category]?.label || item.category}</span>
                    </div>
                  </td>
                  <td className="cell-title">{item.name}</td>
                  <td>{item.site_name}</td>
                  <td className="cell-location">
                    <MapPin size={14} />
                    <span>{item.location || 'Not specified'}</span>
                  </td>
                  <td>
                    {item.is_asset ? '-' : (
                      <span className={item.min_qty && item.current_qty < item.min_qty ? 'text-warning' : ''}>
                        {item.current_qty}{item.min_qty ? ` / ${item.min_qty}` : ''}
                      </span>
                    )}
                  </td>
                  <td><StockBadge item={item} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Modals */}
      <EquipmentFormModal
        isOpen={showFormModal}
        onClose={() => { setShowFormModal(false); setSelectedItem(null); }}
        onSave={handleSave}
        item={selectedItem}
        sites={sites}
      />
      <EquipmentDetailModal
        isOpen={showDetailModal}
        onClose={() => { setShowDetailModal(false); setSelectedItem(null); }}
        item={selectedItem}
        onEdit={handleEdit}
        onAdjustStock={handleAdjustStock}
      />
    </div>
  );
};

export default EquipmentPage;
